<script setup lang="ts">
import type { PointerDownOutsideEvent } from "reka-ui"
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from "reka-ui"
import { computed, watch } from "vue"

export type BaseModalRekaTransition = "fade" | "flip-side" | "slide-up"
export type BaseModalRekaSize = "default" | "xl"

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    description?: string
    transition?: BaseModalRekaTransition
    size?: BaseModalRekaSize
    allowOverflow?: boolean
    closeOnBackdropClick?: boolean
  }>(),
  {
    transition: "fade",
    size: "default",
    allowOverflow: false,
    closeOnBackdropClick: false,
  },
)

const emit = defineEmits<{
  "update:open": [value: boolean]
  "open": []
  "close": []
}>()

const panelWidthBySize: Record<BaseModalRekaSize, string> = {
  default: "w-[768px]",
  xl: "w-[1024px]",
}

const panelSizeClass = computed(() => panelWidthBySize[props.size])
const contentVariantClass = computed(() =>
  props.transition === "fade" ? undefined : `base-modal-content--${props.transition}`,
)
const effectiveDescription = computed(() => props.description ?? props.title)

watch(
  () => props.open,
  (val) => {
    val ? emit("open") : emit("close")
  },
)

function onOpenChange(val: boolean) {
  emit("update:open", val)
}

function onPointerDownOutside(event: PointerDownOutsideEvent) {
  if (!props.closeOnBackdropClick) {
    event.preventDefault()
  }
}
</script>

<template>
  <DialogRoot :open="open" @update:open="onOpenChange">
    <DialogPortal>
      <DialogOverlay
        class="base-modal-overlay"
        :class="{ 'perspective-[1000px]': transition === 'flip-side' }"
      >
        <DialogContent
          class="base-modal-content"
          :class="[panelSizeClass, contentVariantClass, { 'overflow-y-visible': allowOverflow }]"
          data-test="base_modal_content"
          @open-auto-focus.prevent
          @pointer-down-outside="onPointerDownOutside"
        >
          <DialogTitle as="h2" class="sr-only">
            {{ title }}
          </DialogTitle>
          <DialogDescription class="sr-only">
            {{ effectiveDescription }}
          </DialogDescription>
          <slot />
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="postcss">
@reference "../../../assets/styles.css";

.base-modal-overlay {
  @apply fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4;
}

.base-modal-content {
  @apply max-w-[85vw] max-h-[85vh] overflow-y-auto outline-none;
}
</style>
